import { TIMEOUT_MS } from '@/constants/devices'

export type LivePoint = { x: number; y: number | null }

export type AlertInterval = { start: string; end?: string; value: string }

export const INACTIVITY_MS = TIMEOUT_MS

/** Tożsamość zakresu. Powstaje z nieprzyciętego początku i pozycji w tablicy, nie z `from`/`to`:
 *  koniec trwającego alarmu rośnie z każdą klatką, a przycięcie do lewej krawędzi nadaje ten sam
 *  początek wszystkim alarmom sprzed widocznego okna. Tablica jest wyłącznie dopisywana. */
export type AlertRange = { from: number; to: number; id: string }

/** Zakresy, w których urządzenie zgłaszało dany stan.
 *
 *  Nie są cięte przestojami. „Urządzenie było w alarmie" i „urządzenie nie nadawało danych" to dwa
 *  niezależne fakty i potrafią zachodzić naraz — dokładnie tak jest, gdy alarm przychodzi razem
 *  z ostatnim odczytem, a chwilę później gaśnie zasilanie. Wycinanie gubiło wtedy alarm w całości,
 *  bo zaczynał się w tej samej chwili co przestój. Rozdziela je teraz kształt: przestój rysuje się
 *  jako pełnowysokie tło, stan urządzenia jako pasek przy górnej krawędzi.
 *
 *  Zakres o zerowej długości przechodzi dalej: rysowanie nadaje paskowi minimalną szerokość, więc
 *  alarm zgłoszony w jednej chwili pozostaje widoczny. Odsiewane są tylko zakresy cofnięte w czasie. */
export const alertRanges = (
  intervals: AlertInterval[],
  type: 'alarm' | 'error',
  firstX: number,
  now: number,
): AlertRange[] => {
  const ranges: AlertRange[] = []

  intervals.forEach((interval, index) => {
    if (interval.value !== type || !interval.start) return

    const start = Math.max(new Date(interval.start).getTime(), firstX)
    const end = interval.end ? new Date(interval.end).getTime() : now

    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return

    ranges.push({ from: start, to: end, id: `${type}:${interval.start}#${index}` })
  })

  return ranges
}

export const updateAlertIntervals = (intervals: AlertInterval[], value: string, isoNow: string): void => {
  const closeOfType = (target: string) => {
    for (let i = intervals.length - 1; i >= 0; i--) {
      if (intervals[i].value === target && !intervals[i].end) intervals[i].end = isoNow
    }
  }
  const openIfNeeded = (openValue: string) => {
    const last = intervals[intervals.length - 1]
    if (!last || last.end || last.value !== openValue) {
      intervals.push({ start: isoNow, value: openValue })
    }
  }

  if (value === 'error') {
    closeOfType('alarm')
    openIfNeeded('error')
  } else if (value === 'alarm') {
    closeOfType('error')
    openIfNeeded('alarm')
  } else {
    for (let i = intervals.length - 1; i >= 0; i--) {
      if (!intervals[i].end) intervals[i].end = isoNow
    }
  }
}
